import { MaritalStatus } from '@tcs/client-portal-api/types/Beneficiaries';
import {
  Beneficiary,
  BeneficiaryType,
  RelationshipType,
} from '@tcs/client-portal-api/types/Beneficiary';
import { Persona } from '@tcs/client-portal-api/types/Persona';
import useProfile from '@tcs/client-portal-common/hooks/useProfile';
import useSession from '@tcs/client-portal-common/hooks/useSession';
import {
  phone_regex,
  ssn_regex,
  tin_ein_regex,
  zipcode_regex,
} from '@tcs/client-portal-common/utils/regularExpressions';
import { STATES } from '@tcs/common/assets/constants';
import { useFormState } from '@tcs/common/components/atoms/Form';
import { isValid, parse } from 'date-fns';
import { compact, first, values } from 'lodash';
import { useMemo } from 'react';
import { array, lazy, number, object, string } from 'yup';
import {
  BENEFICIARIES_ERRORS,
  BENEFICIARY_ERRORS,
} from './BeneficiariesValidationErrors';

export const isCommunityPropertyState = (profile: any) => {
  if (!profile?.address?.state) {
    return false;
  }
  const state = STATES.find(({ Id }) => Id === profile.address.state);
  return state?.IsCommunityPropertyState ?? false;
};

const validateBeneficiariesAllocationTotal = (beneficiaries: Beneficiary[]) => {
  if (beneficiaries) {
    return beneficiaries?.every(
      (beneficiary) => Number(beneficiary.percentage ?? 0) > 0,
    );
  }
  return true;
};

const validateBeneficiariesPercentageTotal = (beneficiaries: Beneficiary[]) => {
  if (!beneficiaries || beneficiaries.length === 0) {
    return true;
  }
  const sum =
    beneficiaries?.reduce(
      (acc, { percentage }) => acc + Number(percentage ?? 0),
      0,
    ) || 0;
  return Number(sum.toFixed(2)) === 100;
};

const hasMiddleName = (middleName = '') => middleName.trim().length > 0;

const getCombinedNameLength = (
  firstName = '',
  middleName = '',
  lastName = '',
) => compact([firstName, middleName, lastName]).join(' ').length;

export const isValidIndividualNameLength = (
  firstName = '',
  middleName = '',
  lastName = '',
) =>
  getCombinedNameLength(firstName, middleName, lastName) <=
  (hasMiddleName(middleName) ? 26 : 27);

export const getIndividualNameLengthErrorMessage = (
  middleName = '',
  errorsMap = BENEFICIARY_ERRORS,
) =>
  hasMiddleName(middleName)
    ? errorsMap.name.withMiddleTooLong
    : errorsMap.name.withoutMiddleTooLong;

export const beneficiaryFormSchema = (errorsMap = BENEFICIARY_ERRORS) =>
  object({
    beneficiaryType: string().required(),
    relationship: string().when('beneficiaryType', {
      is: BeneficiaryType.Individual,
      then: (schema) =>
        schema
          .max(25, errorsMap.relationship.max)
          .oneOf(
            [
              'Spouse',
              'Non-Spouse Individual',
              'Notarized Spouse',
              'Notarized Non-Spouse',
            ],
            errorsMap.relationship.invalid,
          )
          .required(errorsMap.relationship.required),
    }),
    entityType: string().when('beneficiaryType', {
      is: BeneficiaryType.Entity,
      then: (schema) =>
        schema
          .oneOf(
            [
              'Estate',
              'Trust',
              'Other',
              'Notarized Estate',
              'Notarized Trust',
              'Notarized Other',
            ],
            errorsMap.entityType.invalid,
          )
          .required(errorsMap.entityType.required),
    }),
    entityName: string().when('beneficiaryType', {
      is: BeneficiaryType.Entity,
      then: (schema) =>
        schema
          .required(errorsMap.entityName.required)
          .max(30, errorsMap.entityName.max),
    }),
    firstName: string().when('beneficiaryType', {
      is: BeneficiaryType.Individual,
      then: (x) =>
        x
          .test('is-valid-length', function (firstName) {
            const { middleName = '', lastName = '' } = this.parent;
            if (isValidIndividualNameLength(firstName, middleName, lastName)) {
              return true;
            }
            return this.createError({
              message: getIndividualNameLengthErrorMessage(
                middleName,
                errorsMap,
              ),
            });
          })
          .required(errorsMap.name.firstRequired),
    }),
    middleName: string().when('beneficiaryType', {
      is: BeneficiaryType.Individual,
      then: (x) =>
        x.test('is-valid-length', function (middleName) {
          if (!middleName) {
            return true;
          }
          const { firstName = '', lastName = '' } = this.parent;
          if (isValidIndividualNameLength(firstName, middleName, lastName)) {
            return true;
          }
          return this.createError({
            message: getIndividualNameLengthErrorMessage(middleName, errorsMap),
          });
        }),
    }),
    lastName: string().when('beneficiaryType', {
      is: BeneficiaryType.Individual,
      then: (x) =>
        x
          .test('is-valid-length', function (lastName) {
            const { firstName = '', middleName = '' } = this.parent;
            if (isValidIndividualNameLength(firstName, middleName, lastName)) {
              return true;
            }
            return this.createError({
              message: getIndividualNameLengthErrorMessage(
                middleName,
                errorsMap,
              ),
            });
          })
          .required(errorsMap.name.lastRequired),
    }),
    dateOfBirth: string().test(
      'valid-date',
      errorsMap.dateOfBirth.invalid,
      (value) => {
        if (!value) {
          return true;
        }
        const parsedDate = parse(value, 'MM/dd/yyyy', new Date());
        if (!isValid(parsedDate)) {
          return false;
        }
        const today = new Date();
        const dateFloor = new Date('1/1/1910');
        return parsedDate < today && parsedDate > dateFloor;
      },
    ),
    socialSecurityNumber: string().when('beneficiaryType', {
      is: BeneficiaryType.Individual,
      then: (x) => x.matches(ssn_regex, errorsMap.identification.invalidSSN),
    }),
    taxIdentificationNumber: string().when('beneficiaryType', {
      is: BeneficiaryType.Entity,
      then: (x) =>
        x.matches(tin_ein_regex, errorsMap.identification.invalidTIN),
    }),
    address: string().max(30, errorsMap.address.max),
    city: string().max(21, errorsMap.city.max),
    state: string().oneOf(
      ['', ...STATES.map(({ Id }) => Id)],
      errorsMap.state.invalid,
    ),
    zipCode: lazy((value) => {
      if (!value || value.length <= 0) {
        return string();
      }

      if (value?.length > 5) {
        return string()
          .matches(zipcode_regex, errorsMap.zipCode.invalid)
          .min(10, errorsMap.zipCode.min9)
          .max(10, errorsMap.zipCode.max);
      }

      return string()
        .matches(zipcode_regex, errorsMap.zipCode.invalid)
        .min(5, errorsMap.zipCode.min5);
    }),
    phone: string().matches(phone_regex, errorsMap.phone.invalid),
    email: string().email(errorsMap.email.invalid).max(50, errorsMap.email.max),
  });

export const beneficiariesListSchema = (profile: any) =>
  object({
    maritalStatus: lazy((value) => {
      if (!value && isCommunityPropertyState(profile)) {
        return string().required('Marital Status is required');
      }
      return string().nullable();
    }),
    primaryBeneficiaries: array().of(
      beneficiaryFormSchema(BENEFICIARIES_ERRORS).concat(
        object().shape({
          percentage: number()
            .positive(
              'All primary beneficiaries must have an allocation greater than 0%',
            )
            .max(
              100,
              'The allocation across all primary beneficiaries must equal 100%',
            )
            .required('All primary beneficiaries must have an allocation'),
        }),
      ),
    ),
    primaryBeneficiariesCount: number()
      .when(['primaryBeneficiaries'], {
        is: (primaryBeneficiaries) => {
          return (
            primaryBeneficiaries === undefined ||
            primaryBeneficiaries.length === 0
          );
        },
        then: (schema) =>
          schema.required('You must have at least 1 primary beneficiary'),
      })
      .when(['primaryBeneficiaries'], {
        is: (primaryBeneficiaries) => {
          return primaryBeneficiaries && primaryBeneficiaries.length > 9;
        },
        then: (schema) =>
          schema.required(
            'You can only have a maximum of 9 primary beneficiaries',
          ),
      })
      .when(['maritalStatus', 'primaryBeneficiaries'], {
        is: (maritalStatus, primaryBeneficiaries) => {
          if (
            isCommunityPropertyState(profile) &&
            maritalStatus &&
            maritalStatus === MaritalStatus.Married
          ) {
            const hasMultipleSpouseBeneficiary =
              primaryBeneficiaries?.filter(
                ({ relationship }) => relationship === RelationshipType.Spouse,
              ).length > 1;

            return hasMultipleSpouseBeneficiary;
          }
          return false;
        },
        then: (schema) =>
          schema.required('You can only have 1 spouse beneficiary'),
      })
      .when(['maritalStatus', 'primaryBeneficiaries'], {
        is: (maritalStatus, primaryBeneficiaries) => {
          if (
            isCommunityPropertyState(profile) &&
            maritalStatus &&
            maritalStatus === MaritalStatus.Married
          ) {
            const hasNonSpouseBeneficiary =
              primaryBeneficiaries?.filter(
                ({ relationship }) =>
                  relationship === RelationshipType.NonSpouse,
              ).length > 0;

            return hasNonSpouseBeneficiary;
          }
          return false;
        },
        then: (schema) =>
          schema.required('You can only have a spouse beneficiary'),
      }),
    primaryBeneficiariesAllocationTotal: number().when(
      'primaryBeneficiaries',
      ([primaryBeneficiaries], schema) =>
        schema.test(
          'primary-beneficiaries-allocation-total',
          'All primary beneficiaries must have an allocation greater than 0%',
          () => validateBeneficiariesAllocationTotal(primaryBeneficiaries),
        ),
    ),
    primaryBeneficiariesPercentageTotal: number().when(
      'primaryBeneficiaries',
      ([primaryBeneficiaries], schema) =>
        schema.test(
          'primary-beneficiaries-percentage-total',
          'The allocation across all primary beneficiaries must equal 100%',
          () => validateBeneficiariesPercentageTotal(primaryBeneficiaries),
        ),
    ),
    contingentBeneficiaries: array().of(
      beneficiaryFormSchema(BENEFICIARIES_ERRORS).concat(
        object().shape({
          percentage: number()
            .positive(
              'All contingent beneficiaries must have an allocation greater than 0%',
            )
            .max(100)
            .required('All primary beneficiaries must have an allocation'),
        }),
      ),
    ),
    contingentBeneficiariesCount: number().when(
      'contingentBeneficiaries',
      ([contingentBeneficiaries], schema) => {
        if (contingentBeneficiaries && contingentBeneficiaries.length > 9) {
          return schema.required(
            'You can only have a maximum of 9 contingent beneficiaries',
          );
        }

        return schema;
      },
    ),
    contingentBeneficiariesAllocationTotal: number().when(
      'contingentBeneficiaries',
      ([contingentBeneficiaries], schema) =>
        schema.test(
          'contingent-beneficiaries-allocation-total',
          'All contingent beneficiaries must have an allocation greater than 0%',
          () => validateBeneficiariesAllocationTotal(contingentBeneficiaries),
        ),
    ),
    contingentBeneficiariesPercentageTotal: number().when(
      'contingentBeneficiaries',
      ([contingentBeneficiaries], schema) =>
        schema.test(
          'contingent-beneficiaries-percentage-total',
          'The allocation across all contingent beneficiaries must equal 100%',
          () => validateBeneficiariesPercentageTotal(contingentBeneficiaries),
        ),
    ),
    singleSpouseBeneficiary: number().when(
      ['primaryBeneficiaries', 'contingentBeneficiaries'],
      {
        is: (primaryBeneficiaries = [], contingentBeneficiaries = []) => {
          const beneficiaries = [
            ...primaryBeneficiaries,
            ...contingentBeneficiaries,
          ];

          const hasMultipleSpouseBeneficiaries =
            beneficiaries.filter(
              ({ relationship }) => relationship === RelationshipType.Spouse,
            ).length > 1;

          return hasMultipleSpouseBeneficiaries;
        },
        then: (x) =>
          x.required('You can only have 1 Spouse beneficiary designation'),
      },
    ),
    primaryBeneficiariesNoSpouse: number().when(
      ['maritalStatus', 'primaryBeneficiaries'],
      {
        is: (maritalStatus, primaryBeneficiaries = []) => {
          const hasMultipleSpouseBeneficiaries =
            primaryBeneficiaries.filter(
              ({ relationship }) => relationship === RelationshipType.Spouse,
            ).length > 0;

          return (
            maritalStatus &&
            maritalStatus === MaritalStatus.Single &&
            hasMultipleSpouseBeneficiaries
          );
        },
        then: (x) =>
          x.required('You cannot have a spouse as a primary beneficiary'),
      },
    ),
    primaryBeneficiariesCPSMarried: string().when(
      ['maritalStatus', 'primaryBeneficiaries'],
      {
        is: (maritalStatus, primaryBeneficiaries) => {
          return (
            isCommunityPropertyState(profile) &&
            maritalStatus === MaritalStatus.Married &&
            primaryBeneficiaries?.filter(
              ({ beneficiaryType, relationship }) =>
                beneficiaryType === BeneficiaryType.Individual &&
                (relationship === RelationshipType.Spouse ||
                  relationship?.startsWith('Notarized')),
            ).length === 0 &&
            primaryBeneficiaries?.filter(
              ({ beneficiaryType, entityType }) =>
                beneficiaryType === BeneficiaryType.Entity &&
                entityType?.startsWith('Notarized'),
            ).length === 0
          );
        },
        then: (schema) =>
          schema.required('You must have a spouse as a primary beneficiary'),
      },
    ),
    primaryBeneficiariesCPSNotMarried: string().when(
      ['maritalStatus', 'primaryBeneficiaries'],
      {
        is: (maritalStatus, primaryBeneficiaries) => {
          return (
            isCommunityPropertyState(profile) &&
            maritalStatus &&
            maritalStatus !== MaritalStatus.Married &&
            primaryBeneficiaries?.filter(
              ({ relationship }) => relationship === RelationshipType.Spouse,
            ).length > 0
          );
        },
        then: (schema) =>
          schema.required('You cannot have a spouse as a primary beneficiary'),
      },
    ),
    contingentBeneficiariesNoSpouse: number().when(
      ['maritalStatus', 'contingentBeneficiaries'],
      {
        is: (maritalStatus, contingentBeneficiaries = []) => {
          const hasMultipleSpouseBeneficiaries =
            contingentBeneficiaries.filter(
              ({ relationship }) => relationship === RelationshipType.Spouse,
            ).length > 0;

          return (
            maritalStatus &&
            maritalStatus === MaritalStatus.Single &&
            hasMultipleSpouseBeneficiaries
          );
        },
        then: (x) =>
          x.required('You cannot have a spouse as a contingent beneficiary'),
      },
    ),
    contingentBeneficiariesCPSMarried: string().when(
      ['maritalStatus', 'contingentBeneficiaries', 'primaryBeneficiaries'],
      {
        is: (maritalStatus, contingentBeneficiaries, primaryBeneficiaries) => {
          if (
            isCommunityPropertyState(profile) &&
            maritalStatus &&
            maritalStatus === MaritalStatus.Married
          ) {
            const hasSpouseBeneficiaries =
              contingentBeneficiaries?.filter(
                ({ relationship }) => relationship === RelationshipType.Spouse,
              ).length > 0;

            // Check if all primary beneficiaries have a notarized relationship
            const allPrimaryBeneficiariesNotarized =
              primaryBeneficiaries?.length > 0 &&
              primaryBeneficiaries.every(
                ({ beneficiaryType, relationship, entityType }) =>
                  (beneficiaryType === BeneficiaryType.Individual &&
                    relationship?.startsWith('Notarized')) ||
                  (beneficiaryType === BeneficiaryType.Entity &&
                    entityType?.startsWith('Notarized')),
              );

            return hasSpouseBeneficiaries && !allPrimaryBeneficiariesNotarized;
          }
          return false;
        },
        then: (schema) =>
          schema.required(
            'You cannot have a spouse as a contingent beneficiary',
          ),
      },
    ),
    contingentBeneficiariesCPSNotMarried: string().when(
      ['maritalStatus', 'contingentBeneficiaries'],
      {
        is: (maritalStatus, contingentBeneficiaries) => {
          return (
            isCommunityPropertyState(profile) &&
            maritalStatus &&
            maritalStatus !== MaritalStatus.Married &&
            contingentBeneficiaries?.filter(
              ({ relationship }) => relationship === RelationshipType.Spouse,
            ).length > 0
          );
        },
        then: (schema) =>
          schema.required(
            'You cannot have a spouse as a contingent beneficiary',
          ),
      },
    ),
    primaryBeneficiariesCPSMarriedNotEntity: string().when(
      ['maritalStatus', 'primaryBeneficiaries'],
      {
        is: (maritalStatus, primaryBeneficiaries) => {
          return (
            isCommunityPropertyState(profile) &&
            maritalStatus &&
            maritalStatus === MaritalStatus.Married &&
            primaryBeneficiaries?.filter(
              ({ beneficiaryType, relationshipType }) =>
                beneficiaryType === BeneficiaryType.Entity &&
                relationshipType in ['Estate', 'Trust', 'Other'],
            ).length > 0
          );
        },
        then: (schema) =>
          schema.required('You cannot have an entity as a primary beneficiary'),
      },
    ),
  });

export const validateAddPrimaryBeneficiary = (
  formState: any,
  profile: any,
  persona: Persona,
) => {
  if (isCommunityPropertyState(profile) && !formState.values.maritalStatus) {
    return {
      disabled: true,
      tooltip:
        'You may begin adding beneficiaries once your marital status has been established.',
    };
  }

  if (formState.values.primaryBeneficiaries?.length >= 9) {
    return {
      disabled: true,
      tooltip: 'You have added the maximum number of beneficiaries allowed.',
    };
  }

  if (
    persona === Persona.Individual &&
    formState.values.maritalStatus === MaritalStatus.Married &&
    formState.values.primaryBeneficiaries?.length >= 1
  ) {
    return {
      disabled: true,
      tooltip: 'You have added the maximum number of beneficiaries allowed.',
    };
  }

  return { disabled: false };
};

export const validateAddContingentBeneficiary = (formState: any) => {
  if (
    !formState.values.primaryBeneficiaries ||
    formState.values.primaryBeneficiaries?.length === 0
  ) {
    return {
      disabled: true,
      tooltip:
        'You must add a Primary Beneficiary before adding Contingent Beneficiaries',
    };
  }

  if (formState.values.contingentBeneficiaries?.length >= 9) {
    return {
      disabled: true,
      tooltip: 'You have added the maximum number of beneficiaries allowed.',
    };
  }

  return { disabled: false };
};

const reduceErrors = (beneficaries = []) =>
  beneficaries.reduce((acc, error) => {
    if (!error) {
      return acc;
    }

    Object.keys(error).forEach((key) => {
      if (key === 'percentage') {
        return;
      }

      acc[key] = error[key];
    });

    return acc;
  }, {});

const beneficiariesErrorsFactory = (beneficiaryType, errors) => {
  const primaryBeneficiariesErrors = {
    primaryBeneficiariesCount: errors.primaryBeneficiariesCount,
    beneficiariesAllocationTotalError:
      errors.primaryBeneficiariesAllocationTotal,
    beneficiariesPercentageTotalError:
      errors.primaryBeneficiariesPercentageTotal,
    communityPropertyStateError:
      errors.singleSpouseBeneficiary ??
      errors.primaryBeneficiariesNoSpouse ??
      errors.primaryBeneficiariesCPSMarried ??
      errors.primaryBeneficiariesCPSNotMarried ??
      errors.primaryBeneficiariesCPSMarriedNotEntity,
    ...reduceErrors(errors.primaryBeneficiaries),
  };

  const contingentBeneficiariesErrors = {
    contingentBeneficiariesCount: errors.contingentBeneficiariesCount,
    beneficiariesAllocationTotalError:
      errors.contingentBeneficiariesAllocationTotal,
    beneficiariesPercentageTotalError:
      errors.contingentBeneficiariesPercentageTotal,
    communityPropertyStateError:
      errors.singleSpouseBeneficiary ??
      errors.contingentBeneficiariesNoSpouse ??
      errors.contingentBeneficiariesCPSMarried ??
      errors.contingentBeneficiariesCPSNotMarried,
    ...reduceErrors(errors.contingentBeneficiaries),
  };

  if (beneficiaryType === 'primaryBeneficiaries') {
    return primaryBeneficiariesErrors;
  }

  if (beneficiaryType === 'contingentBeneficiaries') {
    return contingentBeneficiariesErrors;
  }

  if (compact(values(primaryBeneficiariesErrors)).length > 0) {
    return primaryBeneficiariesErrors;
  }

  return contingentBeneficiariesErrors;
};

export const useBeneficiariesErrors = (
  accountNumber: string,
  beneficiaryType?: string,
) => {
  const { data: profile } = useProfile(accountNumber);
  const formState = useFormState();

  const errors = useMemo(
    () => beneficiariesErrorsFactory(beneficiaryType, formState.errors),
    [beneficiaryType, formState.errors],
  );

  const {
    claims: { clientPortalPersona: persona },
  } = useSession();

  const addBeneficiaryAction = useMemo(() => {
    if (beneficiaryType === 'primaryBeneficiaries') {
      return validateAddPrimaryBeneficiary(formState, profile, persona);
    }
    return validateAddContingentBeneficiary(formState);
  }, [beneficiaryType, formState, profile]);

  const latestError = useMemo(() => {
    if (errors.beneficiariesAllocationTotalError) {
      return 'If you have added beneficiaries under Primary Beneficiaries and/or Contingent Beneficiaries, each beneficiary allocation for that section must be greater than 0%. Please adjust entries to ensure that the the each allocation is greater than 0%';
    }

    if (
      !errors.beneficiariesAllocationTotalError &&
      errors.beneficiariesPercentageTotalError
    ) {
      return `If you have added beneficiaries under Primary Beneficiaries and/or Contingent Beneficiaries, allocation for that section must equal 100%. Please adjust entries to ensure that the the total allocation is exactly 100%`;
    }

    return first(compact(values(errors)));
  }, [errors]);

  return {
    errors,
    hasErrors: compact(values(errors)).length > 0,
    latestError,
    isCommunityPropertyState: isCommunityPropertyState(profile),
    addBeneficiaryAction,
  };
};
